import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Phone, Mail, MapPin, Globe, Instagram, Facebook,
  MessageCircle, Save, Sparkles, Loader2, Image as ImageIcon, X,
} from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { formatUSPhone } from "../lib/format";
import { Button } from "../components/ui/button";
import { FormField } from "../components/ui/form-field";
import { clearCompanyCache } from "../lib/companySettings";
import { AdminLayout } from "../components/AdminLayout";
import { useBranding } from "../branding/BrandingContext";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  address: "",
  website: "",
  instagram: "",
  facebook: "",
  whatsapp: "",
  tagline: "",
  logo_path: "",
  primary_color: "#2563EB",
  accent_color: "#0EA5E9",
  app_subtitle: "Operações de Limpeza",
};

export default function CompanySettings() {
  const nav = useNavigate();
  const { refresh: refreshBranding } = useBranding();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company-settings");
        setForm({ ...EMPTY, ...data });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function upd(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  const [uploadingLogo, setUploadingLogo] = useState(false);
  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Logo muito grande (máx 10MB)");
      return;
    }
    setUploadingLogo(true);
    try {
      const compressed = await compressImage(file, { maxDim: 800, quality: 0.9 });
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("folder", "company_logo");
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      upd("logo_path", data.path);
      toast.success("Logo enviada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  }

  async function save(e) {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.put("/company-settings", form);
      clearCompanyCache();
      await refreshBranding();
      toast.success("Dados da empresa salvos");
      nav("/dashboard");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 size={16} className="animate-spin" /> Carregando…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="company-settings-form">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
          <Building2 size={22} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Dados da Empresa</h1>
          <p className="text-slate-600 text-sm mt-1 leading-snug">
            Essas informações aparecem nos invoices, recibos, faturas e PDFs gerados pelo sistema.
          </p>
        </div>
      </div>

      {(!form.logo_path || !form.tagline) && (
        <div
          data-testid="company-completion-cta"
          className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
        >
          <div className="w-9 h-9 rounded-lg bg-amber-200 text-amber-800 flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-amber-900">
              Complete sua identidade visual
            </div>
            <ul className="text-sm text-amber-800 mt-1 space-y-0.5">
              {!form.logo_path && (
                <li>• <strong>Logo da empresa</strong> ausente — todos os PDFs sairão sem marca.</li>
              )}
              {!form.tagline && (
                <li>• <strong>Slogan</strong> em branco — espaço extra abaixo do nome nos PDFs.</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Identidade */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
          <Sparkles size={13} /> Identidade
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nome da empresa" required>
            <input
              data-testid="company-name"
              className="cm-input"
              placeholder="Ex: Clean Manager USA"
              value={form.name}
              onChange={(e) => upd("name", e.target.value)}
            />
          </FormField>
          <FormField label="Slogan" hint="Frase curta que aparece abaixo do nome (opcional)">
            <input
              data-testid="company-tagline"
              className="cm-input"
              placeholder="Ex: limpeza premium em Boston"
              value={form.tagline}
              onChange={(e) => upd("tagline", e.target.value)}
            />
          </FormField>
          <FormField
            label="Subtítulo do sistema"
            hint="Texto que aparece embaixo do nome no topo e no menu do painel"
          >
            <input
              data-testid="company-app-subtitle"
              className="cm-input"
              placeholder="Ex: Operações de Limpeza"
              value={form.app_subtitle}
              onChange={(e) => upd("app_subtitle", e.target.value)}
            />
          </FormField>
        </div>
      </section>

      {/* Prévia do topo do painel */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="brand-preview-section">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
          <ImageIcon size={13} /> Prévia do topo
        </div>
        <p className="text-xs text-slate-500">
          É assim que sua marca aparece no topo e no menu do painel.
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="brand-preview">
          <img
            src={form.logo_path ? fileUrl(form.logo_path) : "/assets/cleanmanager-emblem.png"}
            alt={form.name || "Logo"}
            className="w-10 h-10 rounded-lg object-contain bg-white flex-shrink-0 border border-slate-200"
            draggable={false}
          />
          <div className="min-w-0">
            <div className="font-bold text-lg leading-tight truncate text-slate-900" data-testid="brand-preview-name">
              {form.name || "Nome da empresa"}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 truncate" data-testid="brand-preview-subtitle">
              {form.app_subtitle || "Operações de Limpeza"}
            </div>
          </div>
        </div>
      </section>

      {/* Contato */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
          <Phone size={13} /> Contato
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Telefone">
            <input
              data-testid="company-phone"
              className="cm-input"
              type="tel"
              inputMode="tel"
              maxLength={14}
              placeholder="(857) 287-9507"
              value={form.phone}
              onChange={(e) => upd("phone", formatUSPhone(e.target.value))}
            />
          </FormField>
          <FormField label="WhatsApp" hint="Formato US: (xxx) xxx-xxxx">
            <input
              data-testid="company-whatsapp"
              className="cm-input"
              type="tel"
              inputMode="tel"
              maxLength={14}
              placeholder="(857) 287-9507"
              value={form.whatsapp}
              onChange={(e) => upd("whatsapp", formatUSPhone(e.target.value))}
            />
          </FormField>
          <FormField label="E-mail">
            <input
              data-testid="company-email"
              type="email"
              className="cm-input"
              placeholder="contato@cleanmanagerusa.com"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
            />
          </FormField>
          <FormField label="Endereço">
            <input
              data-testid="company-address"
              className="cm-input"
              placeholder="11 Edith St, Everett, MA 02149"
              value={form.address}
              onChange={(e) => upd("address", e.target.value)}
            />
          </FormField>
        </div>
      </section>

      {/* Online */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
          <Globe size={13} /> Online & Redes sociais
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Site" hint="URL completa (opcional)">
            <input
              data-testid="company-website"
              className="cm-input"
              placeholder="https://cleanmanagerusa.com"
              value={form.website}
              onChange={(e) => upd("website", e.target.value)}
            />
          </FormField>
          <FormField label="Instagram" hint="@usuario ou URL">
            <input
              data-testid="company-instagram"
              className="cm-input"
              placeholder="@cleanmanagerusa"
              value={form.instagram}
              onChange={(e) => upd("instagram", e.target.value)}
            />
          </FormField>
          <FormField label="Facebook" hint="Perfil/página">
            <input
              data-testid="company-facebook"
              className="cm-input"
              placeholder="facebook.com/cleanmanagerusa"
              value={form.facebook}
              onChange={(e) => upd("facebook", e.target.value)}
            />
          </FormField>
        </div>
      </section>

      {/* Logo da empresa */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
          <ImageIcon size={13} /> Logo
        </div>
        <p className="text-sm text-slate-600">
          A logo aparecerá no topo de invoices, recibos e orçamentos em PDF.
          PNG/JPG até 5MB recomendado.
        </p>
        <div className="flex items-center gap-4">
          {form.logo_path ? (
            <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src={fileUrl(form.logo_path)}
                alt="logo"
                className="w-full h-full object-contain"
                data-testid="company-logo-preview"
              />
              <button
                type="button"
                data-testid="company-logo-remove"
                onClick={() => upd("logo_path", "")}
                className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow"
                aria-label="Remover logo"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
              <ImageIcon size={32} />
            </div>
          )}
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="company-logo-input"
              disabled={uploadingLogo}
            />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50">
              {uploadingLogo ? (
                <><Loader2 size={14} className="animate-spin" /> Enviando…</>
              ) : (
                <>Selecionar logo</>
              )}
            </span>
          </label>
        </div>
      </section>

      {/* Preview */}
      <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
          🎨 Branding & cores
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label="Cor primária">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.primary_color || "#2563EB"}
                onChange={(e) => upd("primary_color", e.target.value)}
                data-testid="company-primary-color"
                className="w-12 h-10 rounded-lg border border-slate-300 cursor-pointer"
              />
              <input
                type="text"
                value={form.primary_color || ""}
                onChange={(e) => upd("primary_color", e.target.value)}
                placeholder="#2563EB"
                className="cm-input flex-1"
              />
            </div>
          </FormField>
          <FormField label="Cor de destaque">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.accent_color || "#0EA5E9"}
                onChange={(e) => upd("accent_color", e.target.value)}
                data-testid="company-accent-color"
                className="w-12 h-10 rounded-lg border border-slate-300 cursor-pointer"
              />
              <input
                type="text"
                value={form.accent_color || ""}
                onChange={(e) => upd("accent_color", e.target.value)}
                placeholder="#0EA5E9"
                className="cm-input flex-1"
              />
            </div>
          </FormField>
          <FormField label="Subtítulo da barra lateral">
            <input
              type="text"
              value={form.app_subtitle || ""}
              onChange={(e) => upd("app_subtitle", e.target.value)}
              placeholder="Operações de limpeza"
              className="cm-input"
              data-testid="company-app-subtitle"
            />
          </FormField>
        </div>
        <div className="mt-4 flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: form.primary_color || "#2563EB" }}
          >
            {(form.name || "C").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-slate-900">{form.name || "Sua Empresa"}</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">
              {form.app_subtitle || "Operações de Limpeza"}
            </div>
          </div>
        </div>
      </section>

      {/* Preview */}
      <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
          🧾 Pré-visualização no cabeçalho de fatura
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-2xl font-bold text-slate-900">
                {form.name || "Clean Manager USA"}
              </div>
              {form.tagline && (
                <div className="text-sm text-slate-600 italic">{form.tagline}</div>
              )}
            </div>
            <div className="text-xs text-slate-600 text-right space-y-0.5">
              {form.phone && <div className="flex items-center gap-1 justify-end"><Phone size={11} />{form.phone}</div>}
              {form.email && <div className="flex items-center gap-1 justify-end"><Mail size={11} />{form.email}</div>}
              {form.address && <div className="flex items-center gap-1 justify-end max-w-xs"><MapPin size={11} />{form.address}</div>}
              {form.website && <div className="flex items-center gap-1 justify-end"><Globe size={11} />{form.website}</div>}
              {form.whatsapp && <div className="flex items-center gap-1 justify-end"><MessageCircle size={11} />WhatsApp: {form.whatsapp}</div>}
              {form.instagram && <div className="flex items-center gap-1 justify-end"><Instagram size={11} />{form.instagram}</div>}
              {form.facebook && <div className="flex items-center gap-1 justify-end"><Facebook size={11} />{form.facebook}</div>}
            </div>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-white py-3 -mx-2 px-2 border-t border-slate-200">
        <Button
          data-testid="company-save-btn"
          type="submit"
          disabled={saving}
        >
          <Save size={15} className="mr-2" />
          {saving ? "Salvando…" : "Salvar dados"}
        </Button>
      </div>
      </form>
    </AdminLayout>
  );
}
